import { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Plus, 
  Search,
  Zap,
  CloudRain,
  Droplets,
  Thermometer,
  MoreVertical,
  Edit,
  Trash2,
  MapPin,
  Sun,
  StopCircle,
  Play,
  Timer,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { triggersService, gardensService, companiesService } from '@/services';
import type { ApiTrigger, CreateTriggerRequest, UpdateTriggerRequest } from '@/types/api';
import { Trigger } from '@/types';
import { TriggerFormDialog } from '@/components/triggers/TriggerFormDialog';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

// Skeleton component
function TriggerCardSkeleton() {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-xl" />
            <div>
              <Skeleton className="h-5 w-40 mb-1" />
              <Skeleton className="h-4 w-20" />
            </div>
          </div>
        </div>
        <Skeleton className="h-4 w-full mb-3" />
        <Skeleton className="h-12 w-full mb-3" />
        <Skeleton className="h-6 w-32" />
      </CardContent>
    </Card>
  );
}

// Helper to convert API trigger to frontend Trigger type
function apiTriggerToFrontend(apiTrigger: ApiTrigger): Trigger {
  return {
    id: apiTrigger.id,
    companyId: apiTrigger.companyId || undefined, // Convert null to undefined for frontend
    name: apiTrigger.name,
    description: apiTrigger.description || undefined,
    category: apiTrigger.category,
    sensorConfig: apiTrigger.sensorConfig || undefined,
    forecastConfig: apiTrigger.forecastConfig || undefined,
    enabled: apiTrigger.enabled,
    zoneAssignments: apiTrigger.zoneAssignments?.map(za => ({
      id: za.id,
      triggerId: apiTrigger.id,
      gardenId: za.gardenId,
      zoneId: za.zoneId,
      controllerId: za.controllerId,
      valveChannel: za.valveChannel,
    })) || [],
    createdAt: new Date(apiTrigger.createdAt),
    updatedAt: new Date(apiTrigger.updatedAt),
  };
}

export default function Triggers() {
  const [searchTerm, setSearchTerm] = useState('');
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTrigger, setSelectedTrigger] = useState<Trigger | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentUser, isSuperAdmin } = useAuth();

  // Query para obtener triggers
  // company_admin: solo ve triggers de su empresa + triggers globales (companyId: null)
  // superadmin: ve todos los triggers
  const { 
    data: triggersData, 
    isLoading, 
    error,
    refetch 
  } = useQuery({
    queryKey: ['triggers', currentUser?.companyId],
    queryFn: async () => {
      // Para company_admin, el backend filtra automáticamente por companyId
      // Para superadmin, no enviamos companyId para ver todos
      const params: { pageSize: number; companyId?: string } = { pageSize: 100 };
      if (!isSuperAdmin && currentUser?.companyId) {
        // El backend filtra automáticamente, pero podemos enviarlo explícitamente si es necesario
        // Según la documentación, company_admin solo ve los de su empresa + globales
      }
      const response = await triggersService.getAll(params);
      if (!response.success) {
        throw new Error(response.error?.message || 'Error al cargar triggers');
      }
      return response.data;
    },
    enabled: !!currentUser, // Solo ejecutar cuando hay usuario autenticado
  });

  // Query para obtener jardines
  const { data: gardensData } = useQuery({
    queryKey: ['gardens'],
    queryFn: async () => {
      const response = await gardensService.getAll({ pageSize: 100 });
      if (!response.success) {
        throw new Error(response.error?.message || 'Error al cargar jardines');
      }
      return response.data;
    },
  });

  // Query para obtener empresas (solo para superadmin)
  const { data: companiesData } = useQuery({
    queryKey: ['companies'],
    queryFn: async () => {
      const response = await companiesService.getAll({ pageSize: 100 });
      if (!response.success) {
        throw new Error(response.error?.message || 'Error al cargar empresas');
      }
      return response.data;
    },
    enabled: isSuperAdmin, // Solo cargar si es superadmin
  });

  // Zonas vinculadas al trigger (v0.3) – para formulario y asignar
  const { data: linkedZonesData } = useQuery({
    queryKey: ['trigger-linked-zones', selectedTrigger?.id],
    queryFn: async () => {
      if (!selectedTrigger?.id) return [];
      const response = await triggersService.getLinkedZones(selectedTrigger.id);
      if (!response.success) return [];
      return (response.data ?? []).map((l) => l.zoneId);
    },
    enabled: formDialogOpen && !!selectedTrigger?.id,
  });

  const initialLinkedZoneIds = linkedZonesData ?? [];

  // Mutation para crear (v0.3: opcionalmente vincular zonas después)
  const createMutation = useMutation({
    mutationFn: async (payload: { data: CreateTriggerRequest; linkedZoneIds?: string[] }) => {
      const res = await triggersService.create(payload.data);
      return { res, linkedZoneIds: payload.linkedZoneIds };
    },
    onSuccess: async ({ res, linkedZoneIds }) => {
      if (res.success && res.data) {
        if (linkedZoneIds?.length) {
          const linkRes = await triggersService.linkZones(res.data.id, linkedZoneIds);
          if (!linkRes.success) {
            toast({ title: 'Trigger creado', description: 'Zonas no se pudieron vincular.', variant: 'destructive' });
          }
        }
        queryClient.invalidateQueries({ queryKey: ['triggers'] });
        queryClient.invalidateQueries({ queryKey: ['trigger-linked-zones'] });
        toast({ title: 'Trigger creado', description: 'El trigger ha sido creado correctamente.' });
        setFormDialogOpen(false);
      } else {
        toast({ title: 'Error', description: res.error?.message, variant: 'destructive' });
      }
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Mutation para actualizar (v0.3: opcionalmente actualizar zonas vinculadas)
  const updateMutation = useMutation({
    mutationFn: async (payload: { id: string; data: UpdateTriggerRequest; linkedZoneIds?: string[] }) => {
      const res = await triggersService.update(payload.id, payload.data);
      return { res, id: payload.id, linkedZoneIds: payload.linkedZoneIds };
    },
    onSuccess: async ({ res, id, linkedZoneIds }) => {
      if (res.success) {
        if (linkedZoneIds !== undefined) {
          const linkRes = await triggersService.linkZones(id, linkedZoneIds);
          if (!linkRes.success) {
            toast({ title: 'Trigger actualizado', description: 'Zonas no se pudieron actualizar.', variant: 'destructive' });
          }
        }
        queryClient.invalidateQueries({ queryKey: ['triggers'] });
        queryClient.invalidateQueries({ queryKey: ['trigger-linked-zones'] });
        toast({ title: 'Trigger actualizado', description: 'El trigger ha sido actualizado correctamente.' });
        setFormDialogOpen(false);
      } else {
        toast({ title: 'Error', description: res.error?.message, variant: 'destructive' });
      }
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Mutation para eliminar
  const deleteMutation = useMutation({
    mutationFn: (id: string) => triggersService.delete(id),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['triggers'] });
        toast({ title: 'Trigger eliminado', description: 'El trigger ha sido eliminado.', variant: 'destructive' });
        setDeleteDialogOpen(false);
        setSelectedTrigger(null);
      } else {
        toast({ title: 'Error', description: response.error?.message, variant: 'destructive' });
      }
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Mutation para toggle enabled (usa endpoint /toggle)
  const toggleMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled?: boolean }) => 
      triggersService.toggle(id, enabled),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['triggers'] });
        const newEnabled = response.data?.enabled ?? false;
        toast({ 
          title: newEnabled ? 'Trigger activado' : 'Trigger desactivado',
          description: `El trigger ha sido ${newEnabled ? 'activado' : 'desactivado'}.`
        });
      } else {
        toast({ title: 'Error', description: response.error?.message, variant: 'destructive' });
      }
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const apiTriggers = triggersData?.items || [];
  const triggers = apiTriggers.map(apiTriggerToFrontend);
  const gardens = gardensData?.items || [];

  // Jardines en formato para selector de zonas (v0.3)
  const gardenOptions = gardens.map((g: { id: string; name: string; companyId: string; zones?: { id: string; name: string }[] }) => ({
    id: g.id,
    name: g.name,
    companyId: g.companyId,
    zones: (g.zones ?? []).map((z) => ({ id: z.id, name: z.name, gardenId: g.id })),
  }));

  const filteredTriggers = triggers.filter(trigger =>
    trigger.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sensorTriggers = filteredTriggers.filter(t => t.category === 'sensor');
  const forecastTriggers = filteredTriggers.filter(t => t.category === 'forecast');

  const handleCreateTrigger = () => {
    setSelectedTrigger(null);
    setFormDialogOpen(true);
  };

  const handleEditTrigger = (trigger: Trigger) => {
    setSelectedTrigger(trigger);
    setFormDialogOpen(true);
  };

  const handleDeleteTrigger = (trigger: Trigger) => {
    setSelectedTrigger(trigger);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (selectedTrigger) {
      deleteMutation.mutate(selectedTrigger.id);
    }
  };

  const handleSaveTrigger = (trigger: Trigger, linkedZoneIds?: string[]) => {
    if (selectedTrigger) {
      const updateData: UpdateTriggerRequest = {
        name: trigger.name,
        description: trigger.description,
        sensorConfig: trigger.sensorConfig,
        forecastConfig: trigger.forecastConfig,
        enabled: trigger.enabled,
        companyId: isSuperAdmin ? (trigger.companyId || null) : undefined,
      };
      updateMutation.mutate({ id: selectedTrigger.id, data: updateData, linkedZoneIds });
    } else {
      const createData: CreateTriggerRequest = {
        name: trigger.name,
        description: trigger.description,
        category: trigger.category,
        sensorConfig: trigger.sensorConfig,
        forecastConfig: trigger.forecastConfig,
        enabled: trigger.enabled,
        companyId: isSuperAdmin ? (trigger.companyId || null) : currentUser?.companyId || undefined,
      };
      createMutation.mutate({ data: createData, linkedZoneIds });
    }
  };

  const handleToggleEnabled = (triggerId: string, currentEnabled: boolean) => {
    // Si no se envía enabled, el backend hace auto-toggle
    toggleMutation.mutate({ id: triggerId, enabled: !currentEnabled });
  };

  // Stats
  const activeCount = triggers.filter(t => t.enabled).length;
  const sensorCount = triggers.filter(t => t.category === 'sensor').length;
  const forecastCount = triggers.filter(t => t.category === 'forecast').length;
  const totalAssignments = triggers.reduce((acc, t) => acc + t.zoneAssignments.length, 0);

  const renderTriggerCard = (trigger: Trigger) => {
    const isSensor = trigger.category === 'sensor';
    const config = isSensor ? trigger.sensorConfig : trigger.forecastConfig;
    
    return (
      <motion.div key={trigger.id} variants={itemVariants}>
        <Card className="hover:shadow-card-hover transition-all duration-300">
          <CardContent className="p-5">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={cn(
                  'p-2.5 rounded-xl',
                  trigger.enabled ? 'bg-primary/10' : 'bg-muted'
                )}>
                  {isSensor ? (
                    trigger.sensorConfig?.sensorType === 'humidity' 
                      ? <Droplets className={cn('w-5 h-5', trigger.enabled ? 'text-primary' : 'text-muted-foreground')} />
                      : <Thermometer className={cn('w-5 h-5', trigger.enabled ? 'text-primary' : 'text-muted-foreground')} />
                  ) : (
                    trigger.forecastConfig?.forecastType === 'rain'
                      ? <CloudRain className={cn('w-5 h-5', trigger.enabled ? 'text-info' : 'text-muted-foreground')} />
                      : <Sun className={cn('w-5 h-5', trigger.enabled ? 'text-warning' : 'text-muted-foreground')} />
                  )}
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">
                    {trigger.name}
                  </h3>
                  <Badge variant="outline" className="text-xs mt-1">
                    {isSensor ? 'Por Sensor' : 'Por Previsión'}
                  </Badge>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch 
                  checked={trigger.enabled} 
                  onCheckedChange={() => handleToggleEnabled(trigger.id, trigger.enabled)}
                  disabled={toggleMutation.isPending}
                />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleEditTrigger(trigger)}>
                      <Edit className="w-4 h-4 mr-2" />
                      Editar
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      className="text-destructive focus:text-destructive"
                      onClick={() => handleDeleteTrigger(trigger)}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Eliminar
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {trigger.description && (
              <p className="text-sm text-muted-foreground mb-3">
                {trigger.description}
              </p>
            )}

            {/* Condition summary */}
            <div className="p-3 rounded-lg bg-muted/50 mb-3">
              {isSensor && trigger.sensorConfig && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">Cuando</span>
                  <Badge variant="outline">
                    {trigger.sensorConfig.sensorType === 'humidity' ? 'Humedad' : 'Temperatura'}
                  </Badge>
                  <span className="font-mono">
                    {trigger.sensorConfig.condition === 'lt' ? '<' : '>'} {trigger.sensorConfig.threshold}
                    {trigger.sensorConfig.sensorType === 'humidity' ? '%' : '°C'}
                  </span>
                </div>
              )}
              {!isSensor && trigger.forecastConfig && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">Cuando previsión de</span>
                  <Badge variant="outline">
                    {trigger.forecastConfig.forecastType === 'rain' ? 'Lluvia' : 'Temperatura'}
                  </Badge>
                  <span className="font-mono">
                    {trigger.forecastConfig.condition === 'rain_probability_gt' 
                      ? `> ${trigger.forecastConfig.threshold}%` 
                      : `${trigger.forecastConfig.condition === 'temperature_gt' ? '>' : '<'} ${trigger.forecastConfig.threshold}°C`}
                  </span>
                </div>
              )}
            </div>

            {/* Action */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Acción:</span>
                {isSensor && trigger.sensorConfig && (
                  <Badge 
                    className={cn(
                      trigger.sensorConfig.action === 'start_irrigation' 
                        ? 'bg-success/10 text-success border-success/20'
                        : 'bg-destructive/10 text-destructive border-destructive/20'
                    )}
                    variant="outline"
                  >
                    {trigger.sensorConfig.action === 'start_irrigation' ? (
                      <>
                        <Play className="w-3 h-3 mr-1" />
                        Riego {trigger.sensorConfig.irrigationDuration} min
                      </>
                    ) : (
                      <>
                        <StopCircle className="w-3 h-3 mr-1" />
                        Detener riego
                      </>
                    )}
                  </Badge>
                )}
                {!isSensor && trigger.forecastConfig && (
                  <Badge 
                    className={cn(
                      trigger.forecastConfig.action === 'add_irrigation'
                        ? 'bg-success/10 text-success border-success/20'
                        : 'bg-destructive/10 text-destructive border-destructive/20'
                    )}
                    variant="outline"
                  >
                    {trigger.forecastConfig.action === 'add_irrigation' ? (
                      <>
                        <Play className="w-3 h-3 mr-1" />
                        Añadir riego {trigger.forecastConfig.irrigationDuration} min
                      </>
                    ) : (
                      <>
                        <StopCircle className="w-3 h-3 mr-1" />
                        Anular riego
                      </>
                    )}
                  </Badge>
                )}
              </div>
              
              {(() => {
                const linkedZoneCount = new Set(trigger.zoneAssignments?.map((za) => za.zoneId) ?? []).size;
                return (
                  <Badge variant="secondary" className="text-xs">
                    <MapPin className="w-3 h-3 mr-1" />
                    {linkedZoneCount === 0 ? 'Toda la empresa' : `${linkedZoneCount} zona(s)`}
                  </Badge>
                );
              })()}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-display font-bold text-foreground">Triggers</h1>
            <p className="text-muted-foreground mt-1">Automatiza acciones basadas en sensores y previsiones meteorológicas</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Skeleton className="h-20" />
          <Skeleton className="h-20" />
          <Skeleton className="h-20" />
          <Skeleton className="h-20" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((i) => <TriggerCardSkeleton key={i} />)}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-display font-bold text-foreground">Triggers</h1>
            <p className="text-muted-foreground mt-1">Automatiza acciones basadas en sensores y previsiones meteorológicas</p>
          </div>
        </div>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <span>{error instanceof Error ? error.message : 'Error al cargar triggers'}</span>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Reintentar
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold text-foreground">
            Triggers
          </h1>
          <p className="text-muted-foreground mt-1">
            Automatiza acciones basadas en sensores y previsiones meteorológicas
          </p>
        </div>
        <Button 
          className="gradient-primary text-white shadow-md hover:shadow-lg transition-shadow"
          onClick={handleCreateTrigger}
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Trigger
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Buscar triggers..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-success/10">
              <Zap className="w-5 h-5 text-success" />
            </div>
            <div>
              <p className="text-2xl font-display font-bold">{activeCount}</p>
              <p className="text-sm text-muted-foreground">Activos</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Droplets className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-display font-bold">{sensorCount}</p>
              <p className="text-sm text-muted-foreground">Por Sensor</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-info/10">
              <CloudRain className="w-5 h-5 text-info" />
            </div>
            <div>
              <p className="text-2xl font-display font-bold">{forecastCount}</p>
              <p className="text-sm text-muted-foreground">Por Previsión</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-warning/10">
              <MapPin className="w-5 h-5 text-warning" />
            </div>
            <div>
              <p className="text-2xl font-display font-bold">{totalAssignments}</p>
              <p className="text-sm text-muted-foreground">Asignaciones</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Sensor-based triggers */}
      {sensorTriggers.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Droplets className="w-5 h-5 text-primary" />
            Triggers por Sensores
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {sensorTriggers.map(renderTriggerCard)}
          </div>
        </div>
      )}

      {/* Forecast-based triggers */}
      {forecastTriggers.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <CloudRain className="w-5 h-5 text-info" />
            Triggers por Previsión
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {forecastTriggers.map(renderTriggerCard)}
          </div>
        </div>
      )}

      {filteredTriggers.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12"
        >
          <Zap className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">
            No hay triggers configurados.
          </p>
          <Button className="mt-4" variant="outline" onClick={handleCreateTrigger}>
            <Plus className="w-4 h-4 mr-2" />
            Crear primer trigger
          </Button>
        </motion.div>
      )}

      {/* Dialogs */}
      <TriggerFormDialog
        open={formDialogOpen}
        onOpenChange={setFormDialogOpen}
        trigger={selectedTrigger}
        onSave={handleSaveTrigger}
        isLoading={createMutation.isPending || updateMutation.isPending}
        companies={companiesData?.items || []}
        currentUser={currentUser}
        isSuperAdmin={isSuperAdmin}
        gardens={gardenOptions}
        initialLinkedZoneIds={initialLinkedZoneIds}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar trigger?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará el trigger "{selectedTrigger?.name}" y todas sus asignaciones.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete} 
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Eliminando...' : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
}
